// @ts-check
// @ts-ignore
import api from '@/lib/api'
import { object, string, boolean } from 'yup'
import {
	PendingAccountConfirmation,
	ProfileLoading,
	ResetState,
	SetAccountRecoverCredentialsId,
	SetAccountRecoverLoginId,
	SetAuthFlowPayload,
	SetAuthFlowWithProvider,
	SetAuthToken,
	SetCurrentCredentials,
	SetCurrentSessionId,
	SetSessions,
	SetRefreshToken,
	SetUserId,
	UpdateCredentials,
	UpdateProfiles,
	UpdateSessions,
	SetAuthReady,
	SetRefreshDeadline,
} from './mutation-type'
import {
	AuthCredentialsTypeEnum,
	OAuthAuthorizedType,
	UserSessionModelStatusEnum,
	UserInfoStatusEnum,
	ESCS_API_URL
// @ts-ignore
} from '@/lib/api.model'
// @ts-ignore
import { throwForbiddenAction, logAndRethrow, logError } from '@/lib/util'
// @ts-ignore
import emitter, { AppEvents } from '@/lib/events'
// @ts-ignore
import router from '@/router'
// @ts-ignore
import Config from '@/config'
// @ts-ignore
import { RouteVerify } from '@/router/routes'

const MONTH_MS = 30 * 24 * 60 * 60 * 1e3

/** @type {import('@/store').ActionTree} */
export default {

	reset({ commit }) {
		commit(ResetState)
	},

	updateAuth(
		{ commit },
		{
			authToken = null,
			refreshToken = null,
			credentialsId = null,
			refreshDeadline = null
		}
	) {
		commit(SetAuthToken, authToken)
		commit(SetRefreshToken, refreshToken)
		commit(SetCurrentCredentials, credentialsId)
		commit(SetRefreshDeadline, refreshDeadline)

		emitter.emit(AppEvents.AuthStateChange, Boolean(authToken && refreshToken))
	},

	async loadUser({ getters, dispatch, commit }) {
		if (getters.isAuthenticated) {
			if (getters.shouldRefreshToken) {
				await dispatch('refreshAuthToken')
			}

			await dispatch('getCurrentSessionInfo')
			await dispatch('getUserCredentials', {
				filters: {
					conditions: {
						metadata: {
							type: AuthCredentialsTypeEnum.EMAIL_AND_PASSWORD
						}
					}
				}
			})
			await dispatch('getUserInfo', {
				isCurrentUser: true,
				userId: getters.userId
			})

			if (getters.isCurrentProfilePending) {
				commit(PendingAccountConfirmation, true)
			}

			commit(SetAuthReady, true)
		}
	},

	/**
	 * @param {{ commit: Function, dispatch: Function }} store
	 * @param {{ email: string; firstName?: string; lastName?: string; username: string; password: string; }} payload
	 */
	async signUp({ commit, dispatch }, payload) {
		const schema = {
			email: string().email().required(),
			password: string().required(),
			username: string().required(),
			firstName: string().nullable(),
			lastName: string().nullable(),
		}

		await object(schema)
			.required()
			.validate(payload)

		const { email, password, username, firstName = '', lastName = '' } = payload
		const params = {
			userInfo: { email, firstName, lastName, username },
			credentials: {
				email,
				password,
				type: AuthCredentialsTypeEnum.EMAIL_AND_PASSWORD
			},
			autoSignIn: true,
		}

		try {
			const res = await api.signUp(params)
			const { session: { authToken, refreshToken } } = res
			const refreshDeadline = Date.now() + MONTH_MS
			const authPayload = 'credentials' in res
				? {
					authToken,
					refreshToken,
					refreshDeadline,
					credentialsId: res.credentials._id,
				}
				: {
					authToken,
					refreshToken,
					refreshDeadline,
				}

			dispatch('updateAuth', authPayload)
			dispatch('effects/updateUserCredentials', authPayload)
			await dispatch('loadUser')
		} catch (error) {
			logError(error)

			if (error.params && error.params.reason === UserInfoStatusEnum.PENDING) {
				commit(PendingAccountConfirmation, true)
			}

			throw error
		}
	},

	/**
	 * @param {{ commit: Function, dispatch: Function, getters: Record<string, any> }} store
	 * @param {{ email: string; password: string; type: 'EMAIL_AND_PASSWORD' } | { _id: string; password: string; type: 'ONE_TIME_PASSWORD' } | { token: string }} payload
	 */
	async signIn({ commit, dispatch, getters }, payload) {
		try {
			const res = await api.signIn({ credentials: payload })

			// Check if user has 2FA enabled. In that case, api responds with
			// a one-time password sent to the user's mailbox.
			if (
				res.nextCredentials &&
				res.nextCredentials.type === AuthCredentialsTypeEnum.ONE_TIME_PASSWORD
			) {
				//window.location.href = `/verify?id=${res.nextCredentials._id}`
				router.push({
					name: RouteVerify,
					query: {
						id: res.nextCredentials._id
					}
				})
				return true
			}

			if ('session' in res) {
				const { session: { authToken, refreshToken } } = res
				const refreshDeadline = Date.now() + MONTH_MS
				const authPayload = 'credentials' in res
					? {
						authToken,
						refreshToken,
						refreshDeadline,
						credentialsId: res.credentials._id,
					}
					: {
						authToken,
						refreshToken,
						refreshDeadline,
					}

				dispatch('updateAuth', authPayload)
				dispatch('effects/updateUserCredentials', authPayload)
				await dispatch('loadUser')
				return false
			}
		} catch (error) {
			logError(error)
			if (error.params && error.params.reason === UserInfoStatusEnum.PENDING) {
				commit(PendingAccountConfirmation, true)
			}

			throw error
		}
	},

	async signInWithSSO({ commit, dispatch }, payload) {
		try {
			const { session } = await api.exchangeSSOToken(
				{
					ssoToken: { token: payload.token },
				},
				{
					baseURL: ESCS_API_URL,
				},
			)
			const { bindings: [res] } = await api.getOAuthBindingInfo(
				{
					context: {
						type: 'USER',
						authToken: session.authToken,
					}
				},
				{
					baseURL: ESCS_API_URL,
				}
			)

			const { authToken } = res
			const refreshDeadline = Date.now() + MONTH_MS
			const authPayload = 'credentials' in res
				? {
					authToken,
					refreshToken: '',
					refreshDeadline,
					credentialsId: res.credentials._id,
				}
				: {
					authToken,
					refreshToken: '',
					refreshDeadline,
				}

			dispatch('updateAuth', authPayload)
			dispatch('effects/updateUserCredentials', authPayload)
			await dispatch('loadUser')
		} catch (error) {
			logError(error)

			if (error.params && error.params.reason === UserInfoStatusEnum.PENDING) {
				commit(PendingAccountConfirmation, true)
			}

			throw error
		}
	},

	/**
	 * @param {{ commit: Function }} param0
	 * @param {{ scope: string, app_id: string, redirect_url: string, code: string, action: string }} payload
	 */
	async setAuthFlowPayload({ commit }, payload) {
		const validationSchema = object()	// TODO: validate auth flow payload
		commit(SetAuthFlowPayload, payload)
	},

	async logout({ dispatch, getters }, { remoteLogout = true } = {}) {
		if (remoteLogout) {
			try {
				await dispatch('terminateSession', { sessionId: getters.currentSessionId })
			} catch (error) {
				logError(error)
			}
		}

		const oAuthFlowMetadata = getters.authFlowPayload
		const isOAuthFlow = getters.isOAuthFlow

		dispatch('updateAuth', {
			authToken: null,
			refreshToken: null,
			credentialsId: null,
			refreshDeadline: null
		})
		dispatch('effects/removeUserCredentials')
		dispatch('reset')
		emitter.emit(AppEvents.AuthStateChange, false)

		if (isOAuthFlow) {
			dispatch('setAuthFlowPayload', oAuthFlowMetadata)
			dispatch('effects/startOAuthFlow', oAuthFlowMetadata)
		}
	},

	async getCurrentSessionInfo({ commit, getters }) {
		try {
			const { session: { _id, userId } } = await api.getCurrentSessionInfo({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				}
			})

			commit(SetCurrentSessionId, _id)
			commit(SetUserId, userId)
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async refreshAuthToken({ getters, dispatch }) {
		try {
			const res = await api.refreshAuthToken({
				session: {
					refreshToken: getters.refreshToken
				}
			})
			const { session: { authToken, refreshToken } } = res

			dispatch('updateAuth', {
				authToken,
				refreshToken,
				refreshDeadline: Date.now() + MONTH_MS,
			})
			dispatch('effects/updateUserCredentials', { authToken, refreshToken })
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {{ getters: Record<string, Function>, commit: Function, dispatch: Function }} store
	 */
	async getUserInfo({ commit, getters }, { userId, isCurrentUser = false }) {
		isCurrentUser && commit(ProfileLoading, true)

		try {
			const { user } = await api.getUserInfo({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				user: {
					_id: userId,
				},
			})

			if (isCurrentUser) {
				commit(UpdateProfiles, user)
				commit(ProfileLoading, false)
			}

			return user
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {{ getters: Record<string, Function>, commit: Function, dispatch: Function }} store
	 * @param {{ id: string; enable2FA: boolean; newPassword: string; }} payload
	 */
	async updateUserCredentials({ getters, commit }, payload) {
		const { newPassword, enable2FA, email } = payload
		let credentialsId = getters.accountRecoverCredentialsId != null
			? getters.accountRecoverCredentialsId
			: getters.currentCredentialsId

		if (!credentialsId && email && newPassword) {
			const {credentials} = await api.addUserCredentials({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				credentials: {
					type: 'EMAIL_AND_PASSWORD',
					email,
					password: newPassword,
					enable2FA:false
				},
			})
			return  credentials
		}

		try {
			const {credentials} =await api.updateUserCredentials({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				credentials: {
					enable2FA,
					email,
					password: newPassword,
					_id: credentialsId,
				},
			})

			if (typeof enable2FA === 'boolean') {
				commit(UpdateCredentials, {
					_id: credentialsId,
					metadata: {
						enable2FA
					}
				})
			}

			if (getters.accountRecoverCredentialsId != null) {
				commit(SetAccountRecoverCredentialsId, null)
			}

			return  credentials
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {{ getters: Record<string, Function>, commit: Function, dispatch: Function }} store
	 * @param {{ _id: string, avatar?: string | null; firstName?: string; lastName?: string; birthday?: string | null; phone?: string | null; gender?: string | null; options?: { enable2FA: boolean; }}} payload
	 */
	async updateUserInfo({ getters, commit }, payload) {
		const schema = {
			_id: string(),
			firstName: string().nullable(true),
			lastName: string().nullable(true),
			avatar: string().nullable(true),
			birthday: string().nullable(true),
			phone: string().nullable(true),
			gender: string().nullable(true),
			options: object({ enable2FA: boolean() }),
		}

		await object(schema)
			.required()
			.validate(payload)

		try {
			const { _id, ...userInfo } = payload

			await api.updateUserInfo({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				user: {
					_id: getters.userId,
				},
				userInfo,
			})

			commit(UpdateProfiles, payload)
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {{ getters: Record<string, Function>, commit: Function, dispatch: Function }} store
	 * @param {{ login: string; }} payload
	 */
	 async restoreUserAccount({}, payload) {
		const { login } = payload

		try {
			await api.restoreUserAccount({
				restore: {
					type: 'email',
					email: login
				}
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async setAccountRecoveryData({ commit }, { id }) {
		commit(SetAccountRecoverCredentialsId, id)
	},

	async uploadAvatar({ getters }, { file }) {
		if (!(file instanceof File)) {
			throw new Error('Missing file or wrong file format.')
		}

		try {
			const uploadRequestRes = await api.createUploadFileRequest({
				context: {
					type: 'USER',
					authToken: getters.authToken
				}
			})

			if (uploadRequestRes == null) {
				throw new Error('Missing API Response.')
			}

			const { file: { _id } } = uploadRequestRes
			const uploadRes = await api.uploadFile({
				file,
				fileUploadId: _id,
				authToken: getters.authToken,
			})

			if (uploadRes.file == null) {
				throw new Error('Missing API Response.')
			}

			const { file: { _id: uploadedFileId } } = uploadRes

			return {
				_id: uploadedFileId,
			}
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async getProfiles({ dispatch, getters }) {
		try {
			await dispatch('getUserCredentials')
			const reqs = getters.credentials.map(({ userId }) =>
				dispatch('getUserInfo', {
					isCurrentUser: true,
					userId
				})
			)

			await Promise.all(reqs)
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async getUserCredentials({ commit, getters }, { filters = {} } = {}) {
		try {
			const { count, rows } = await api.getUserCredentials({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				filters,
			})

			const [ credentials ] = rows
			if (credentials) {
				commit(UpdateCredentials, credentials)
			}

			return { count, rows }
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async deleteUserCredentials({ getters }, { id }) {
		try {
			await api.deleteUserCredentials({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				credentials: {
					_id: id
				}
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async deleteUserAccount({ dispatch, getters }) {
		try {
			await api.deleteUserAccount({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				user: {
					_id: getters.userId,
				}
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async grantAuthorizationCode({ getters }, { permissions, applicationId, redirectUrl }) {
		if (getters.authFlowPayload == null) {
			throwForbiddenAction()
		}

		try {
			const res = await api.grantAuthorizationCode({
				permissions,
				redirectUrl,
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				application: {
					_id: applicationId
				},
			})
			return res
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async authorize({ getters }, { applicationId, code, redirectUrl, permissions }) {
		try {
			const context = getters.isAuthenticated
				? {
					type: OAuthAuthorizedType.AUTHORIZED,
					context: {
						type: 'USER',
						authToken: getters.authToken,
					}
				}
				: { type: OAuthAuthorizedType.UNAUTHORIZED }

			const res = await api.authorize({
				...context,
				code,
				redirectUrl,
				permissions,
				application: {
					_id: applicationId,
				},
			})
			return res
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async authorizeGameAccount({ dispatch, getters }) {
		if (getters.authFlowPayload == null) {
			throwForbiddenAction()
		}

		try {
			const {
				applicationId,
				code,
				redirectUrl,
				permissions,
			} = getters.authFlowPayload
			const res = await dispatch(
				'authorize',
				{
					applicationId,
					code,
					redirectUrl,
					permissions,
				}
			)

			if ('session' in res) {
				const { session: { authToken, refreshToken } } = res
				const refreshDeadline = Date.now() + MONTH_MS

				dispatch('updateAuth', {
					authToken,
					refreshToken,
					refreshDeadline,
				})
				dispatch('effects/updateUserCredentials', {
					authToken,
					refreshToken,
					refreshDeadline,
				})
				await dispatch('getCurrentSessionInfo')
				await dispatch('getUserInfo', {
					isCurrentUser: true,
					userId: getters.userId
				})
			}

		} catch (error) {
			logAndRethrow(error)
		}
	},

	async checkApplicationIsBoundToUser({ getters }) {
		try {
			if (getters.authFlowPayload == null) {
				throwForbiddenAction()
			}

			const res = await api.checkApplicationIsBoundToUser({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				user: {
					_id: getters.userId,
				},
				application: {
					_id: getters.authFlowPayload.applicationId,
				},
				permissions: getters.authFlowPayload.permissions
			})
			return res.applicationBoundToUser
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async checkAuthNeedsUserAction({ getters }) {
		try {
			if (getters.authFlowPayload == null) {
				throwForbiddenAction()
			}

			const { isNeeded } = await api.checkNeedsConfirmation({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				type: 'APPLICATION_ACCESS',
				application: {
					_id: getters.authFlowPayload.applicationId,
				},
				permissions: getters.authFlowPayload.permissions
			})
			return isNeeded
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async setAuthFlowWithProvider({ commit }, value) {
		commit(SetAuthFlowWithProvider, value)
	},

	async confirmEmail({}, { actionId, token }) {
		try {
			await api.confirmAction({
				confirmAction: {
					_id: actionId,
					token,
				}
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async confirmDeleteAccount({}, { actionId, token }) {
		try {
			await api.confirmAction({
				confirmAction: {
					_id: actionId,
					token,
				}
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async resendConfirmEmail({ getters }) {
		try {
			await api.sendConfirmationEmail({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async terminateSession({ getters, commit }, { sessionId }) {
		if (!getters.isAuthenticated) {
			throwForbiddenAction()
		}

		try {
			const payload = {
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				session: {
					_id: sessionId,
				},
			}

			await api.terminateSession(payload)

			const session = getters.sessions.find(entry => entry._id === sessionId)

			if (session) {
				commit(UpdateSessions, {
					...session,
					status: UserSessionModelStatusEnum.TERMINATED,
				})
			} else {
				Config.isLoggingEnabled && console.warn(`Sessions not found locally "${ sessionId }".`)
			}
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async terminateAllSessions({ getters, dispatch }) {
		const reqs = getters.sessions
			.filter(({ status }) => status === UserSessionModelStatusEnum.ACTIVE)
			.map(({ _id }) => dispatch('terminateSession', { sessionId: _id }))

		try {
			await Promise.all(reqs)
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async loadUserSessions({ getters, commit }) {
		try {
			const { count, rows } = await api.getUserSessions({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				user: {
					_id: getters.userId
				},
				filters: {
					conditions: {
						status: UserSessionModelStatusEnum.ACTIVE,
					}
				}
			})

			if (count > 0) {
				commit(SetSessions, rows)
			}
		} catch (error) {
			logError(error)
			return error
		}
	},

	async createApplication({ getters }, payload) {
		try {
			await api.createApplication({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				application: {
					...payload
				},
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async getApplications({ getters }, filters = {}) {
		try {
			const { rows } = await api.getApplications({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				filters
			})

			return rows
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async getConnectedApps({ dispatch, getters }) {
		const { count, rows } = await api.getUserCredentials({
			context: {
				type: 'USER',
				authToken: getters.authToken,
			},
			filters: {
				conditions: {
					metadata: {
						type: AuthCredentialsTypeEnum.GAME_BINDING
					}
				}
			},
		})

		if (count === 0) return []

		const appReqs = rows.map(async entry => {
			const payload = {
				conditions: {
					_id: entry.metadata.applicationId
				},
			}

			try {
				const res = await dispatch('getApplications', payload)

				if (res.length === 1) {
					return {
						...entry,
						metadata: {
							...entry.metadata,
							application: res[0]
						}
					}
				}

				return entry
			} catch (error) {
				logError(error)
				return entry
			}
		})

		const apps = await Promise.all(appReqs)
		return apps.filter(entry => entry != null)
	},


	async getConnectedAccounts({ dispatch, getters }) {
		const { count, rows } = await api.getUserCredentials({
			context: {
				type: 'USER',
				authToken: getters.authToken,
			},
			filters: {
				conditions: {
					metadata: {
						type: AuthCredentialsTypeEnum.SOCIAL_LOGIN
					}
				}
			},
		})

		if (count === 0) return []

		return rows
	},

	async deleteApplication({ getters }, { id }) {
		try {
			await api.deleteApplication({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				application: {
					_id: id
				},
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async updateApplication({ getters }, payload) {
		try {
			const { application } = await api.updateApplication({
				context: {
					type: 'USER',
					authToken: getters.authToken,
				},
				application: {
					...payload
				},
			})
			return application
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async createAuthUrl({ }, { type, redirectUrl, state }) {
		try {
			return await api.socialLogin({
				service: {
					type: type,
					action: {
						type: 'CREATE_AUTH_URL',
						redirectUrl,
						state,
					},
				},
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

	async socialLogin({ getters }, { type, code, user }) {
		const context = getters.isAuthenticated ? {
			context: {
				type: 'USER',
				authToken: getters.authToken,
			}
		} : {}
		try {
			return await api.socialLogin({
				...context,
				service: {
					type: type,
					action: {
						type: 'LOGIN',
						code,
						user,
					},
				},
			})
		} catch (error) {
			logAndRethrow(error)
		}
	},

}
